import React, { forwardRef, useCallback, useState } from 'react';
import { RenderNode } from '@contentful/rich-text-react-renderer';
import { BLOCKS } from '@contentful/rich-text-types';
import Image from 'next/legacy/image';

import { IPlanInfo20Fields } from '../../../types/contentful';

import CollapsibleContainer from '../Collapsible/CollapsibleContainer';
import RichText from '../RichText';

import yellowPlanBadge from '@/images/yellow-plan-badge.svg';
import plus from '@/images/plus.svg';
import greenCheck from '@/images/unordered-list-green-check.svg';
import greyCross from '@/images/greyed-cross.svg';

export const CollapsiblePlanInfo = forwardRef<
  HTMLDivElement,
  IPlanInfo20Fields
>(
  (
    {
      planName,
      price,
      color,
      badge,
      description,
      expandField,
      collapseField,
      enabledFields,
      disabledFields,
      button,
      useLightText
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);

    const RICH_TEXT_OVERRIDES: RenderNode = {
      [BLOCKS.LIST_ITEM]: (node, children) => (
        <li className="mb-4 pl-2">{children}</li>
      )
    };

    const toggleSeeMore = useCallback(() => setIsOpen(!isOpen), [isOpen]);

    return (
      <div
        className="relative flex h-full w-full"
        role="group"
        aria-label={planName}
      >
        <div
          className={`flex w-full flex-col items-center justify-between overflow-hidden rounded-[32px] border-2 border-black bg-black shadow-[-4px_4px_0px_0px_#000] ${
            isOpen ? 'h-full' : 'h-fit'
          }`}
        >
          <div
            className="flex h-fit max-h-[126px] w-full flex-col justify-center rounded-t-[32px] p-8"
            style={{
              backgroundColor: color ? `#${color}` : undefined
            }}
          >
            {!!badge && (
              <div className="absolute right-[24px] flex h-[63px] w-[68px] flex-col items-center justify-center text-center tablet:right-[32px] desktop:right-[4px] desktop:top-[-35px] desktop:h-[90px] desktop:w-[90px]">
                <Image
                  src={yellowPlanBadge}
                  alt="Best value plan"
                  className="h-full w-full"
                />
                <p className="typography-body-3 desktop:typography-body-1 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-bold">
                  {badge}
                </p>
              </div>
            )}
            <div className="flex flex-col border-b-0">
              <h3
                className={`typography-display-4 m-0 font-medium mobile:text-[28px] tablet:text-[26px] ${
                  useLightText ? 'text-white' : 'text-black'
                }`}
              >
                {planName}
              </h3>
              <p
                className={`typography-body-1 ${
                  useLightText ? 'text-white' : 'text-black'
                }`}
              >
                {price}
              </p>
            </div>
          </div>

          <div className="flex min-h-[128px] w-full flex-1 flex-col border-y-[2.5px] border-black bg-white p-8">
            {description && (
              <div
                ref={ref}
                className="flex w-full desktop:p-0 desktop:[&>p]:text-[18px]"
              >
                <RichText data={description} />
              </div>
            )}

            {isOpen && (
              <CollapsibleContainer>
                <div
                  className={`mt-6 flex flex-1 flex-col ${
                    button ? 'mb-6' : 'mb-0'
                  } [&>p:not(:last-child)]:mb-0`}
                >
                  <RichText
                    data={enabledFields}
                    overrideOptions={{
                      [BLOCKS.UL_LIST]: (_node, children) => (
                        <ul
                          className="m-0 list-outside pl-8"
                          style={{ listStyleImage: `url(${greenCheck.src})` }}
                        >
                          {children}
                        </ul>
                      ),
                      ...RICH_TEXT_OVERRIDES
                    }}
                  />
                  <RichText
                    colour="collapsibleBorder"
                    data={disabledFields}
                    overrideOptions={{
                      [BLOCKS.UL_LIST]: (_node, children) => (
                        <ul
                          className="m-0 list-outside pl-8"
                          style={{ listStyleImage: `url(${greyCross.src})` }}
                        >
                          {children}
                        </ul>
                      ),
                      ...RICH_TEXT_OVERRIDES
                    }}
                  />
                </div>
                <div className="flex">
                  <RichText data={button} />
                </div>
              </CollapsibleContainer>
            )}
          </div>

          <div
            className="ease flex h-fit max-h-[90px] w-full cursor-pointer flex-row items-center rounded-b-[32px] bg-white p-8 transition-[border-radius] duration-[250ms]"
            onClick={toggleSeeMore}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleSeeMore();
              }
            }}
            role="button"
            tabIndex={0}
            data-testid="btn-see-more"
          >
            <Image
              src={plus}
              alt={isOpen ? 'Close section' : 'Open section'}
              className="ease h-5 w-5 transition-transform duration-[250ms]"
              style={{ transform: isOpen ? 'rotate(45deg)' : 'none' }}
            />
            <p
              className="typography-body-1 mx-auto my-0 font-medium"
              data-testid="btn-expand"
            >
              {isOpen ? collapseField : expandField}
            </p>
          </div>
        </div>
      </div>
    );
  }
);
